import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { businessAPI } from '../../services/api';
import BusinessTaxDeclarations from '../../components/BusinessTaxDeclarations';
import { LocationPicker } from '../../components/maps/LocationMap';
import {
  ROLE_LABELS, STATUS_LABELS, TYPE_LABELS, cleanLocationPayload,
  emptyLocation, errorMessage,
} from '../admin/businessRegistryUi';
import './BusinessWorkspacePage.css';

const PROFILE_FIELDS = [
  ['business_name', 'व्यवसाय / Trade name', 'text'],
  ['business_category', 'वर्ग / Category', 'text'],
  ['phone', 'फोन / Phone', 'tel'],
  ['email', 'ई-मेल / Email', 'email'],
  ['website', 'वेबसाइट / Website', 'text'],
  ['opening_hours', 'वेळ / Opening hours', 'text'],
  ['contact_person', 'संपर्क व्यक्ती / Contact person', 'text'],
  ['contact_person_phone', 'संपर्क फोन / Contact phone', 'tel'],
  ['business_image_url', 'व्यवसाय प्रतिमा URL', 'text'],
];

const editableProfile = business => ({
  business_name: business?.business_name || '', business_category: business?.business_category || '',
  description: business?.description || '', address: business?.address || '', phone: business?.phone || '',
  email: business?.email || '', website: business?.website || '', products_services: business?.products_services || '',
  business_image_url: business?.business_image_url || '', opening_hours: business?.opening_hours || '',
  contact_person: business?.contact_person || '', contact_person_phone: business?.contact_person_phone || '',
});

const editableLocation = location => ({
  ...location,
  branch_name: location.branch_name || '', address_line_1: location.address_line_1 || '',
  address_line_2: location.address_line_2 || '', locality: location.locality || '',
  ward_number: location.ward_number || '', pincode: location.pincode || '',
  floor_area_sqft: location.floor_area_sqft ?? '', signage_area_sqft: location.signage_area_sqft ?? '',
  latitude: location.latitude ?? '', longitude: location.longitude ?? '',
  valid_from: location.valid_from ? String(location.valid_from).slice(0, 10) : '',
  valid_to: location.valid_to ? String(location.valid_to).slice(0, 10) : '',
  new_property_uid: '',
});

export default function BusinessWorkspacePage() {
  const { tenantSlug } = useParams();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [profile, setProfile] = useState(editableProfile(null));
  const [locations, setLocations] = useState([]);
  const [newLocation, setNewLocation] = useState({ ...emptyLocation(), is_primary: false });
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  const applyDetail = useCallback((data, fallbackScopes = []) => {
    setSelected(data);
    setProfile(editableProfile(data));
    setLocations((data.locations || []).map(editableLocation));
    setNewLocation({ ...emptyLocation(), is_primary: !(data.locations || []).some(location => location.status === 'active' && location.is_primary) });
    setScopes(data.granted_scopes || fallbackScopes);
  }, []);

  const loadBusinesses = useCallback(async () => {
    setLoading(true); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.getMine(tenantSlug);
      const rows = response.data.businesses || [];
      setBusinesses(rows);
      if (rows.length) {
        const target = rows[0];
        const detail = await businessAPI.getMineById(tenantSlug, target.business_uid);
        applyDetail(detail.data, target.granted_scopes || []);
      } else {
        setSelected(null); setScopes([]);
      }
    } catch (requestError) {
      setMessage({ type: 'error', text: errorMessage(requestError, 'व्यवसाय खाते लोड करता आले नाही.') });
    } finally { setLoading(false); }
  }, [applyDetail, tenantSlug]);

  useEffect(() => {
    if (isAuthenticated && tenantSlug) loadBusinesses();
  }, [isAuthenticated, loadBusinesses, tenantSlug]);

  const openBusiness = async business => {
    setLoading(true); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.getMineById(tenantSlug, business.business_uid);
      applyDetail(response.data, business.granted_scopes || []);
    } catch (requestError) {
      setMessage({ type: 'error', text: errorMessage(requestError, 'व्यवसाय तपशील लोड करता आला नाही.') });
    } finally { setLoading(false); }
  };

  const hasScope = scope => scopes.includes(scope);

  const saveProfile = async event => {
    event.preventDefault();
    if (!hasScope('manage_profile')) return;
    const phone = profile.phone.replace(/[\s().-]/g, '');
    if (!/^\+?\d{7,15}$/.test(phone)) return setMessage({ type: 'error', text: 'फोनमध्ये 7 ते 15 अंक आवश्यक आहेत.' });
    setSaving('profile'); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.updateMine(tenantSlug, selected.business_uid, {
        version: selected.version,
        ...Object.fromEntries(Object.entries(profile).map(([key, value]) => [key, value.trim() || null])),
      });
      applyDetail(response.data, scopes);
      setBusinesses(current => current.map(item => item.business_uid === response.data.business_uid ? { ...item, ...response.data, granted_scopes: scopes } : item));
      setMessage({ type: 'success', text: 'व्यवसाय प्रोफाइल अद्ययावत झाले.' });
    } catch (requestError) {
      setMessage({ type: 'error', text: errorMessage(requestError, 'प्रोफाइल जतन करता आले नाही.') });
    } finally { setSaving(''); }
  };

  const updateLocationState = (id, key, value) => setLocations(current => current.map(location => location.id === id ? { ...location, [key]: value } : location));

  const validateLocation = location => {
    if (String(location.address_line_1 || '').trim().length < 2) return 'पत्त्याची पहिली ओळ आवश्यक आहे.';
    if (location.pincode && !/^\d{6}$/.test(location.pincode)) return 'पिनकोड 6 अंकी असावा.';
    return '';
  };

  const addLocation = async () => {
    const issue = validateLocation(newLocation);
    if (issue) return setMessage({ type: 'error', text: issue });
    setSaving('new-location'); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.addMyLocation(tenantSlug, selected.business_uid, {
        version: selected.version, ...cleanLocationPayload(newLocation),
      });
      applyDetail(response.data, scopes);
      setMessage({ type: 'success', text: 'व्यवसाय ठिकाण जोडले.' });
    } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'ठिकाण जोडता आले नाही.') }); }
    finally { setSaving(''); }
  };

  const saveLocation = async location => {
    const issue = validateLocation(location);
    if (issue) return setMessage({ type: 'error', text: issue });
    const payload = cleanLocationPayload(location);
    if (location.new_property_uid) payload.property_uid = location.new_property_uid;
    else delete payload.property_uid;
    setSaving(`location-${location.id}`); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.updateMyLocation(tenantSlug, selected.business_uid, location.id, { version: selected.version, ...payload });
      applyDetail(response.data, scopes); setMessage({ type: 'success', text: 'ठिकाण अद्ययावत झाले.' });
    } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'ठिकाण जतन करता आले नाही.') }); }
    finally { setSaving(''); }
  };

  const closeLocation = async location => {
    const reason = window.prompt('ठिकाण बंद करण्याचे कारण:');
    if (reason === null) return;
    if (reason.trim().length < 3) return setMessage({ type: 'error', text: 'कारण किमान 3 अक्षरांचे असावे.' });
    setSaving(`location-${location.id}`); setMessage({ type: '', text: '' });
    try {
      const response = await businessAPI.closeMyLocation(tenantSlug, selected.business_uid, location.id, { version: selected.version, reason: reason.trim() });
      applyDetail(response.data, scopes); setMessage({ type: 'success', text: 'ठिकाण इतिहासासह बंद केले.' });
    } catch (requestError) { setMessage({ type: 'error', text: errorMessage(requestError, 'ठिकाण बंद करता आले नाही.') }); }
    finally { setSaving(''); }
  };

  if (authLoading) return <div className="owner-business-state">खाते तपासत आहोत…</div>;
  if (!isAuthenticated) return <Navigate to={`/login?gp=${tenantSlug}`} replace />;

  return (
    <div className="owner-business-workspace">
      <header className="owner-business-hero"><div><span>सुरक्षित करदाता कार्यक्षेत्र</span><h1>माझे व्यवसाय / My businesses</h1><p>{user?.full_name}, आपल्या मंजूर व्यवसाय नोंदी, ठिकाणे आणि कर प्रवेश येथे व्यवस्थापित करा.</p></div><Link to={`/gp/${tenantSlug}/my-taxes`}>कर पहा / View taxes →</Link></header>

      {message.text && <div className={`owner-business-message ${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>{message.text}{message.type === 'error' && selected && <button type="button" onClick={() => openBusiness(selected)}>पुन्हा लोड करा</button>}</div>}

      {loading && <div className="owner-business-state">व्यवसाय नोंदी लोड होत आहेत…</div>}
      {!loading && !businesses.length && <div className="owner-business-state"><h2>मंजूर व्यवसाय प्रवेश नाही</h2><p>ग्रामपंचायत कार्यालयाने व्यवसायाशी आपले खाते जोडून “view” परवानगी दिल्यानंतर नोंद येथे दिसेल.</p><Link to={`/gp/${tenantSlug}/contact`}>कार्यालयाशी संपर्क साधा</Link></div>}

      {businesses.length > 0 && <div className="owner-business-layout">
        <aside className="owner-business-list" aria-label="माझे व्यवसाय">
          {businesses.map(item => <button type="button" key={item.business_uid} className={selected?.business_uid === item.business_uid ? 'active' : ''} onClick={() => openBusiness(item)}><strong>{item.business_name}</strong><span>{TYPE_LABELS[item.business_type] || item.business_type}</span><small>{(item.granted_scopes || []).join(' · ')}</small></button>)}
        </aside>

        {selected && <main className="owner-business-detail">
          <div className="owner-business-title"><div><h2>{selected.business_name}</h2><p>{selected.business_uid} · v{selected.version}</p></div><span>{STATUS_LABELS[selected.verification_status] || selected.verification_status}</span></div>
          <div className="owner-scope-row" aria-label="मंजूर परवानग्या">{scopes.map(scope => <span key={scope}>{scope.replaceAll('_', ' ')}</span>)}</div>

          <section className="owner-compliance-cta"><div><span>परवाने, NOC आणि नूतनीकरण</span><h3>व्यवसाय अनुपालन / Business compliance</h3><p>लागू नियम पहा, खाजगी कागदपत्रे सुरक्षितपणे सादर करा आणि अर्जाची स्थिती तपासा.</p></div><Link to={`/gp/${tenantSlug}/my-businesses/compliance?business=${selected.business_uid}`}>अनुपालन कार्यक्षेत्र उघडा →</Link></section>

          <section>
            <h3>व्यवसाय ओळख / Registered identity</h3>
            <dl className="owner-business-facts"><div><dt>कायदेशीर नाव</dt><dd>{selected.legal_name || '—'}</dd></div><div><dt>रचना</dt><dd>{selected.constitution_type?.replaceAll('_', ' ')}</dd></div><div><dt>नोंदणी</dt><dd>{selected.registration_number || '—'}</dd></div><div><dt>GSTIN</dt><dd>{selected.gstin || '—'}</dd></div><div><dt>PAN शेवटची 4</dt><dd>{selected.pan_last4 || '—'}</dd></div><div><dt>कर पात्रता</dt><dd>{selected.tax_readiness?.ready ? 'तयार / Ready' : 'कार्यालयीन तपासणी आवश्यक'}</dd></div></dl>
          </section>

          <section>
            <h3>संबंधित पक्ष / Party roles</h3>
            <div className="owner-role-list">{(selected.party_roles || []).filter(role => role.status === 'active').map(role => <article key={role.id}><strong>{role.party?.display_name || role.party?.legal_name}</strong><span>{ROLE_LABELS[role.role_type] || role.role_type}</span><span>{role.ownership_share == null ? '' : `${role.ownership_share}%`}</span><small>{role.is_primary ? 'Primary' : ''}{role.is_liable_party ? ' · Tax liable' : ''}</small></article>)}</div>
          </section>

          <section>
            <div className="owner-section-title"><h3>प्रोफाइल माहिती / Profile</h3>{!hasScope('manage_profile') && <span>फक्त वाचन / View only</span>}</div>
            <form className="owner-profile-form" onSubmit={saveProfile}>
              {PROFILE_FIELDS.map(([key, label, type]) => <label key={key}><span>{label}</span><input type={type} value={profile[key]} disabled={!hasScope('manage_profile')} onChange={event => setProfile(current => ({ ...current, [key]: event.target.value }))} /></label>)}
              <label className="wide"><span>पत्ता / Address</span><textarea rows="2" value={profile.address} disabled={!hasScope('manage_profile')} onChange={event => setProfile(current => ({ ...current, address: event.target.value }))} /></label>
              <label className="wide"><span>वर्णन / Description</span><textarea rows="3" value={profile.description} disabled={!hasScope('manage_profile')} onChange={event => setProfile(current => ({ ...current, description: event.target.value }))} /></label>
              <label className="wide"><span>उत्पादने व सेवा / Products &amp; services</span><textarea rows="3" value={profile.products_services} disabled={!hasScope('manage_profile')} onChange={event => setProfile(current => ({ ...current, products_services: event.target.value }))} /></label>
              {hasScope('manage_profile') && <button type="submit" disabled={Boolean(saving)}>{saving === 'profile' ? 'जतन होत आहे…' : 'प्रोफाइल जतन करा'}</button>}
            </form>
          </section>

          <section>
            <div className="owner-section-title"><h3>व्यवसाय ठिकाणे / Locations</h3>{!hasScope('manage_locations') && <span>फक्त वाचन / View only</span>}</div>
            <div className="owner-location-list">{locations.map((location, index) => <article key={location.id} className={location.status !== 'active' ? 'closed' : ''}>
              <header><strong>{location.branch_name || `ठिकाण ${index + 1}`}</strong><span>{location.status}{location.is_primary ? ' · primary' : ''}</span></header>
              <div className="owner-location-grid">
                <label><span>शाखा नाव</span><input value={location.branch_name} disabled={!hasScope('manage_locations') || location.status !== 'active'} onChange={event => updateLocationState(location.id, 'branch_name', event.target.value)} /></label>
                <label><span>पत्ता ओळ 1</span><input value={location.address_line_1} disabled={!hasScope('manage_locations') || location.status !== 'active'} onChange={event => updateLocationState(location.id, 'address_line_1', event.target.value)} /></label>
                <label><span>परिसर</span><input value={location.locality} disabled={!hasScope('manage_locations') || location.status !== 'active'} onChange={event => updateLocationState(location.id, 'locality', event.target.value)} /></label>
                <label><span>वॉर्ड</span><input value={location.ward_number} disabled={!hasScope('manage_locations') || location.status !== 'active'} onChange={event => updateLocationState(location.id, 'ward_number', event.target.value)} /></label>
                <label><span>नवीन Property UID (ऐच्छिक)</span><input value={location.new_property_uid} disabled={!hasScope('manage_locations') || location.status !== 'active'} onChange={event => updateLocationState(location.id, 'new_property_uid', event.target.value)} /></label>
              </div>
              <LocationPicker
                id={`owner-business-location-${location.id}`}
                latitude={location.latitude}
                longitude={location.longitude}
                onChange={coordinates => setLocations(current => current.map(item => item.id === location.id ? { ...item, ...coordinates } : item))}
                disabled={!hasScope('manage_locations') || location.status !== 'active'}
                label={`${location.branch_name || `ठिकाण ${index + 1}`} / Map location`}
              />
              {hasScope('manage_locations') && location.status === 'active' && <footer><button type="button" disabled={Boolean(saving)} onClick={() => saveLocation(location)}>बदल जतन</button><button type="button" className="danger" disabled={Boolean(saving)} onClick={() => closeLocation(location)}>ठिकाण बंद</button></footer>}
            </article>)}</div>

            {hasScope('manage_locations') && <div className="owner-new-location"><h4>नवीन ठिकाण</h4><div className="owner-location-grid"><label><span>शाखा नाव</span><input value={newLocation.branch_name} onChange={event => setNewLocation(current => ({ ...current, branch_name: event.target.value }))} /></label><label><span>पत्ता ओळ 1 *</span><input value={newLocation.address_line_1} onChange={event => setNewLocation(current => ({ ...current, address_line_1: event.target.value }))} /></label><label><span>परिसर</span><input value={newLocation.locality} onChange={event => setNewLocation(current => ({ ...current, locality: event.target.value }))} /></label><label><span>वॉर्ड</span><input value={newLocation.ward_number} onChange={event => setNewLocation(current => ({ ...current, ward_number: event.target.value }))} /></label><label><span>पिनकोड</span><input value={newLocation.pincode} maxLength="6" inputMode="numeric" onChange={event => setNewLocation(current => ({ ...current, pincode: event.target.value }))} /></label><label><span>Property UID (ऐच्छिक)</span><input value={newLocation.property_uid} onChange={event => setNewLocation(current => ({ ...current, property_uid: event.target.value }))} /></label><label className="check"><input type="checkbox" checked={newLocation.is_primary} onChange={event => setNewLocation(current => ({ ...current, is_primary: event.target.checked }))} /><span>प्राथमिक ठिकाण</span></label></div><LocationPicker id="owner-new-business-location" latitude={newLocation.latitude} longitude={newLocation.longitude} onChange={coordinates => setNewLocation(current => ({ ...current, ...coordinates }))} label="नवीन ठिकाण / New location on map" /><button type="button" onClick={addLocation} disabled={Boolean(saving)}>+ ठिकाण जोडा</button></div>}
          </section>

          {hasScope('submit_tax_declaration') && <BusinessTaxDeclarations
            tenantSlug={tenantSlug}
            businessUid={selected.business_uid}
            mode="self"
            onNotice={setMessage}
          />}

          {(hasScope('view') || hasScope('manage_tax') || hasScope('pay') || hasScope('view_receipts') || hasScope('download_receipts')) && <section className="owner-tax-cta"><div><h3>व्यवसाय कर / Business tax</h3><p>तुमच्या मंजूर परवानग्यांनुसार मागण्या, भरणा आणि पावत्या हाताळा.</p></div><Link to={`/gp/${tenantSlug}/my-taxes`}>कर कार्यक्षेत्र उघडा →</Link></section>}
        </main>}
      </div>}
    </div>
  );
}
